import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import { Fonts, mediaQuery } from '@styles/index';

const TagsStack = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  width: 100%;
`;

const TagsRow = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px;
  width: 100%;

  ${({ $stacked }) =>
    $stacked
      ? `
    padding: 0;
    flex-wrap: nowrap;
  `
      : ''}

  ${({ $preventWrapOnMobile }) =>
    $preventWrapOnMobile
      ? `
    ${mediaQuery.uptoMobile} {
      flex-wrap: nowrap;
    }
  `
      : ''}
`;

const TypeBadge = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background: var(--color-green-2);
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-i-white);
  white-space: nowrap;
`;

const SubscriptionBadge = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${({ $isPrime }) => ($isPrime ? '2px' : '4px')};
  height: 24px;
  padding: ${({ $isPrime }) => ($isPrime ? '0 6px 1px' : '0 8px')};
  border: ${({ $borderColor, $isPrime }) =>
    $isPrime ? `0.923px solid ${$borderColor}` : `1px solid ${$borderColor}`};
  border-radius: ${({ $isPrime }) => ($isPrime ? '4.615px' : '4px')};
  font-size: 12px;
  line-height: ${({ $isPrime }) => ($isPrime ? '14.768px' : '16px')};
  font-weight: ${Fonts.SEMIBOLD};
  color: ${({ $textColor }) => $textColor};
  text-transform: ${({ $isPrime }) => ($isPrime ? 'capitalize' : 'none')};
  letter-spacing: ${({ $isPrime }) => ($isPrime ? '0.24px' : '0')};
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background: var(--color-base-light-0);
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $grow }) => ($grow ? 'flex: 1 0 0;' : 'flex-shrink: 0;')}

  ${({ $equalFlexOnMobile }) =>
    $equalFlexOnMobile
      ? `
    ${mediaQuery.uptoMobile} {
      flex: 1 0 0;
      min-width: 0;
    }
  `
      : ''}
`;

const BadgeIcon = styled.img`
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border: 1px solid ${({ $color }) => $color};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: ${Fonts.SEMIBOLD};
`;

const SubscriptionBadgeChip = ({ badge }) => (
  <SubscriptionBadge
    $borderColor={badge.borderColor}
    $textColor={badge.textColor}
    $isPrime={badge.isPrime}
  >
    {badge.icon ? (
      <BadgeIcon
        src={getImage(badge.icon)}
        width={badge.iconSize}
        height={badge.iconSize}
        alt=""
      />
    ) : null}
    {badge.label}
  </SubscriptionBadge>
);

const TestSeriesListingTestCardTags = ({
  isTestSeries,
  subscriptionBadge,
  statusBadge,
  questionCount,
  durationMinutes,
  testCount,
  dateRangeLabel
}) => {
  if (isTestSeries) {
    return (
      <TagsStack>
        <TagsRow $stacked>
          <TypeBadge>Test series</TypeBadge>
          <SubscriptionBadgeChip badge={subscriptionBadge} />
        </TagsRow>
        <TagsRow $stacked>
          <MetaBadge $grow $equalFlexOnMobile>
            {testCount} tests
          </MetaBadge>
          {dateRangeLabel ? (
            <MetaBadge $grow $equalFlexOnMobile>
              {dateRangeLabel}
            </MetaBadge>
          ) : null}
        </TagsRow>
      </TagsStack>
    );
  }

  return (
    <TagsRow $preventWrapOnMobile>
      <SubscriptionBadgeChip badge={subscriptionBadge} />
      {statusBadge ? (
        <StatusBadge $color={statusBadge.color}>
          {statusBadge.label}
        </StatusBadge>
      ) : null}
      {questionCount > 0 ? (
        <MetaBadge $grow $equalFlexOnMobile>
          {questionCount} questions
        </MetaBadge>
      ) : null}
      <MetaBadge $equalFlexOnMobile>{durationMinutes} min</MetaBadge>
    </TagsRow>
  );
};

export default TestSeriesListingTestCardTags;

SubscriptionBadgeChip.propTypes = {
  badge: PropTypes.shape({
    borderColor: PropTypes.string.isRequired,
    textColor: PropTypes.string.isRequired,
    isPrime: PropTypes.bool.isRequired,
    icon: PropTypes.string.isRequired,
    iconSize: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired
};

TestSeriesListingTestCardTags.propTypes = {
  isTestSeries: PropTypes.bool.isRequired,
  subscriptionBadge: SubscriptionBadgeChip.propTypes.badge,
  statusBadge: PropTypes.shape({
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired
  }),
  questionCount: PropTypes.number,
  durationMinutes: PropTypes.number,
  testCount: PropTypes.number,
  dateRangeLabel: PropTypes.string
};

TestSeriesListingTestCardTags.defaultProps = {
  statusBadge: null,
  questionCount: 0,
  durationMinutes: 0,
  testCount: 0,
  dateRangeLabel: ''
};
